/* eslint-disable @typescript-eslint/no-use-before-define */
import { useSettings } from "@app-composables/settings";
import usei18n from "@app-i18n/index";
import useCompanyRestClient from "@app-rest/company";
import { PolicyType, type SignupBody } from "@app-rest/company.interfaces";
import { resolveAny } from "@app-rest/index";
import { getReservationHome } from "@app-router/helpers";
import router from "@app-router/index";
import { cartPage } from "@app-router/routes/reservations";
import useAppStore from "@app-store/app";
import useCartStore from "@app-store/cart";
import useCompanyStore from "@app-store/company";
import useLangStore from "@app-store/lang";
import { clearReservationKeysPersistence } from "@app-store/reservation-flux";
import useSystemStore from "@app-store/system";
import EventsManager from "@app-utilities/events-manager";
import { deepToRaw, timeout } from "@app-utilities/func";
import { modalOk, modalPrivacy, modalWarning } from "@app-utilities/modals";
import { trackUserEvent } from "@app-utilities/observability";
import { AppInsightsEvent } from "@app-utilities/observability-events";
import { setAzurePopupWindow } from "@app-utilities/popup";
import { waitForSystem } from "@app-utilities/system";
import { AccountInfo, AuthenticationResult, AuthError, EventType, InteractionRequiredAuthError, PublicClientApplication } from "@azure/msal-browser";
import { useObservability } from "@qamf/observability";
import { h, ref } from "vue";
import { type RouteLocation } from "vue-router";
import { Action, Module, Mutation, VuexModule } from "vuex-class-modules";

import { ensureSetupSettings } from "@/boot";

import store from ".";
import { IBaseContext, IPersistentModule, resolveMutationContext, usePersistentModule } from "./persistent.plugin";

const appStore = useAppStore();


interface IAppAzurePolicies {
	signUpSignInPolicyId: string,
	resetPasswordPolicyId: string,
	editProfilePolicyId: string
}
export type AzureADB2CFlow = keyof IAppAzurePolicies
export interface ILoginRedirectTo extends Pick<RouteLocation, "name" | "params" | "query"> {
	flow?: AzureADB2CFlow
	language: string
}
export interface IUserBase {
	AccessToken: string | null;
}
export interface ISignupParameters {
	companyId: number,
	privacyData: SignupBody
}
export interface IUserData extends IBaseUserData {
	LastName: string;
	isLoggedIn: boolean;
	isGuestWithData: boolean;
}
export interface IBaseUserData {
	FirstName: string;
	Phone: string;
	Email: string;
}
export interface IPhone {
	Number: string,
	Type: string
}
export interface IFrequentBowler {
	CenterId: number,
	CustomerId: number
}
export interface IPrivacyChoice {
	PrivacyName: PolicyType,
	PrivacyVersion: number,
	Accepted: boolean
}
export interface IAssociatedSystem {
	CenterId: number
}
export interface ICustomerInfo {
	AzureOId: string,
	AzureId: number,
	FirstName: string,
	LastName: string,
	Emails: string[],
	Phones: IPhone[],
	FrequentBowler: IFrequentBowler,
	PrivacyChoices: IPrivacyChoice[],
	AssociatedCenters: IAssociatedSystem[]
}
export interface IIdTokenClaims {
	at_hash: string;
	aud: string;
	auth_time: number;
	/**
	 *
	 * @type {string[]}
	 * @memberof IIdTokenClaims
	 * @name emails
	 * @returns list of array of the user
	 */
	emails: string[];
	exp: number;
	extension_Phone: string,
	/**
	 *
	 *
	 * @type {string}
	 * @memberof IIdTokenClaims
	 * @name family_name
	 * @description Last Name of the user
	 */
	family_name: string;
	/**
	 *
	 *
	 * @type {string}
	 * @memberof IIdTokenClaims
	 * @name given_name
	 * @description First Name of the user
	 */
	given_name: string;
	iat: number;
	iss: string;
	/**
	 *
	 *
	 * @type {string}
	 * @memberof IIdTokenClaims
	 * @name name
	 * @description First + Last Name of the user
	 */
	name: string;
	nbf: number;
	nonce: string;
	/**
	 *
	 *
	 * @type {string}
	 * @memberof IIdTokenClaims
	 * @name oid
	 * @description Object Identifier description
	 */
	oid: string;
	sub: string;
	tfp: string;
	ver: string;
}
export interface IUserFull extends IUserBase, IUserData {
	AccountInfo: AccountInfo | null;
	reservationsHistory: IReservationKeyHistoryItem[];
}
interface IReservationKeyHistoryItem {
	key: string,
	createdAt: Date
}
interface IAuthError extends Error {
	errorCode: string
	errorMessage: string
}

@Module class UserStoreFactory extends VuexModule implements IPersistentModule {
	AccessToken: string | null = null;
	FirstName = "";
	LastName = "";
	Email = "";
	Phone = "";
	AccountInfo: AccountInfo | null = null;
	CustomerInfo: ICustomerInfo | null = null;
	SessionExpireAt: Date | null = null;
	reservationsHistory: IReservationKeyHistoryItem[] = [];
	isLinkedToCompany = false;
	isLinkedToSystem = false;

	isLoggedIn() {
		return Boolean(this.AccessToken && appStore.isAccessTokenValid());
	}

	isGuestWithData() {
		return !this.isLoggedIn() && Boolean(this.Email);
	}

	isSessionExpired() {
		return this.isLoggedIn() && this.SessionExpireAt && Date.now() > +this.SessionExpireAt;
	}

	userData(): IUserFull {
		return {
			FirstName: this.FirstName,
			LastName: this.LastName,
			Email: this.Email,
			Phone: this.Phone,
			isLoggedIn: this.isLoggedIn(),
			isGuestWithData: this.isGuestWithData(),
			AccessToken: this.AccessToken,
			AccountInfo: this.AccountInfo,
			reservationsHistory: this.reservationsHistory
		};
	}

	@Mutation clearData() {
		this.AccessToken = null;
		this.FirstName = "";
		this.LastName = "";
		this.Email = "";
		this.Phone = "";
		this.AccountInfo = null;
		this.SessionExpireAt = null;
		this.reservationsHistory = [];
		this.CustomerInfo = null;
		this.isLinkedToCompany = false;
		this.isLinkedToSystem = false;
	}

	@Mutation clearPersistedData() {
		// triggers IndexedDB user deletion only (not from store)
	}

	@Mutation setUserData(userData: Partial<IUserFull> | null) {
		this.AccessToken = userData?.AccessToken ?? null;
		this.FirstName = userData?.FirstName ?? "";
		this.LastName = userData?.LastName ?? "";
		this.Email = userData?.Email ?? "";
		this.Phone = userData?.Phone ?? "";
		this.AccountInfo = userData?.AccountInfo ?? null;
		if (userData?.reservationsHistory)
			this.reservationsHistory = userData.reservationsHistory;

		this.CustomerInfo = null;
		this.isLinkedToCompany = false;
		this.isLinkedToSystem = false;
	}

	@Mutation setCustomerInfo(data: ICustomerInfo | null) {
		this.CustomerInfo = data;
	}

	@Mutation setIsLinkedToCompany(state: boolean) {
		this.isLinkedToCompany = state;
	}

	@Mutation setIsLinkedToSystem(state: boolean) {
		this.isLinkedToSystem = state;
	}

	private _findReservationKeyHistoryIndex(key: string) {
		if (!this.reservationsHistory.length) return -1;
		return this.reservationsHistory.findIndex(el => el.key === key);
	}

	@Mutation reservationsHistoryAdd(key: string) {
		const index = this._findReservationKeyHistoryIndex(key);
		if (index !== -1) return;

		this.reservationsHistory.push({
			key, createdAt: new Date()
		});
	}

	@Mutation reservationsHistoryRemove(key: string) {
		const index = this._findReservationKeyHistoryIndex(key);
		if (index < 0) return;
		this.reservationsHistory.splice(index, 1);
	}

	@Mutation cleanExpiredReservationsFromHistory() {
		const expired: IReservationKeyHistoryItem[] = [];
		const notExpired: IReservationKeyHistoryItem[] = [];
		this.reservationsHistory.forEach(el => {
			const isExpired = useCartStore().isExpired(Date.now(), el.createdAt);
			if (isExpired) expired.push(el);
			else notExpired.push(el);
		});
		this.reservationsHistory = notExpired;
		clearReservationKeysPersistence(expired.map(el => el.key));
	}

	@Action login() {
		return loginPopup();
	}

	@Action signup() {
		return loginPopup("signup");
	}

	@Action async completeUserSignup(data: ISignupParameters) {
		const restClient = useCompanyRestClient();
		const systemId = Number(useSystemStore().info?.Id);
		const response = await restClient.linkCustomerToCompany(data.companyId, data.privacyData);
		if (response.fetchResponse?.ok || response.error?.data?.Error?.Code === "CustomerExists") {
			this.setIsLinkedToCompany(true);
			await this.syncLinkToSystem({ companyId: data.companyId, systemId });
		}
	}

	@Action async syncLinkToSystem(data: { companyId: number, systemId: number }) {
		const restClient = useCompanyRestClient();
		const response = await restClient.linkCustomerToSystem(data.companyId, data.systemId);
		this.setIsLinkedToSystem(response.fetchResponse?.ok ?? false);
	}

	@Action async ensureCustomerInfo() {
		if (this.CustomerInfo) return;
		const restClient = useCompanyRestClient();
		const companyId = useCompanyStore().Id;
		const systemId = Number(useSystemStore().info?.Id);
		const response = await restClient.getCustomer(companyId);

		this.setCustomerInfo(!response.fetchResponse?.ok ? null : response.data);
		this.setIsLinkedToCompany(response.fetchResponse?.ok ?? false);
		const currSystemIsLinked = Boolean(response?.data?.AssociatedCenters?.find(el => el.CenterId === systemId));

		this.setIsLinkedToSystem(currSystemIsLinked);
	}

	@Action async initModuleData(context: IBaseContext) {
		const { result, error } = await tryGetUserSessionTokens();
		const claims = result?.idTokenClaims as IIdTokenClaims | undefined;
		if (result && claims && !error) {
			const tokens = result.idTokenClaims as IIdTokenClaims;
			const Email = tokens.emails[tokens.emails.length - 1];
			const key = await userPersistenceKey({ Email });
			const accessCtx = context.access();
			const [persistenceData] = await resolveMutationContext(accessCtx.get<IUserFull>(key));
			this.setUserData({
				AccessToken: result.accessToken,
				FirstName: claims.given_name,
				Email: claims.emails[claims.emails.length - 1],
				LastName: claims.family_name,
				Phone: claims.extension_Phone,
				AccountInfo: result.account,
				reservationsHistory: persistenceData ? persistenceData.reservationsHistory : []
			});
		} else {
			const accessCtx = context.access();
			const guestUserKey = await userPersistenceKey();
			const [guestData] = await resolveMutationContext(accessCtx.get<IUserFull>(guestUserKey));
			this.setUserData(guestData);
		}
		appStore.setAccessTokenInfo(result);
	}
}

const moduleName = "user";

let UseStore: UserStoreFactory | null;

function useUserStore() {
	if (UseStore) return UseStore;

	const mod = UseStore = new UserStoreFactory({ store, name: moduleName });

	mod.$watch(state => state.userData(), (newUser, oldUser) => {
		if (newUser?.AccessToken !== oldUser?.AccessToken)
			EventsManager.emitUserChanged(newUser, oldUser);
	});
	return mod;
}

export default useUserStore;

async function userPersistenceKey(user?: Pick<IUserFull, "Email"> | null) {
	const guestPersistenceKeyBase = "guest";
	await waitForSystem();
	const systemId = Number(useSystemStore().info?.Id);
	if (user)
		return user.Email;
	else if (useUserStore().isLoggedIn())
		return useUserStore().userData().Email;
	else if (systemId)
		return `${guestPersistenceKeyBase}-${systemId}`;
	else
		return guestPersistenceKeyBase;
}
export const UserModuleInit = usePersistentModule(moduleName, useUserStore(), {
	async onMutation(context) {
		const key = await userPersistenceKey();
		if (!context) return;
		const accessCtx = context.access();
		if (context.mutationsContains("clearPersistedData"))
			return await resolveMutationContext(accessCtx.delete(key));

		const data = deepToRaw(useUserStore().userData());
		await resolveMutationContext(accessCtx.put(data, key));
	}
});

/**
 * MSAL / Azure AD B2C popup authentication plumbing.
 *
 * MSAL v5 contract to keep in mind when touching this code:
 * - Popup flows complete via the redirect bridge page served at
 *   `${location.origin}/redirect` (see redirect.html): it parses the response
 *   and broadcasts it back to this window over a BroadcastChannel. That page
 *   must exist, be served, and be registered as "<origin>/redirect" in the B2C
 *   app registration. EVERY origin (localhost, staging slots, production) needs
 *   its own entry — otherwise B2C rejects the request with redirect_uri_mismatch
 *   and bounces the popup to a default registered URI, where the bridge runs on
 *   the wrong origin and the response never reaches this window.
 * - v5 no longer detects the popup being closed before the flow completes: the
 *   popup promise would hang until popupBridgeTimeout (60s) expires, leaving
 *   "msal.interaction.status" stuck in sessionStorage and breaking any later
 *   attempt. watchPopupClosed() restores the v4 "user_cancelled" behavior, and
 *   `overrideInteractionInProgress: true` lets a new attempt cancel a dangling
 *   one instead of throwing interaction_in_progress.
 * - The popup window reference only exists in the POPUP_OPENED event payload:
 *   we capture it there both for the close watcher and for the app-level popup
 *   tracking (overlay, focus, navigation guard) via setAzurePopupWindow().
 */
const clientPromises = new Map<AzureADB2CFlow, Promise<PublicClientApplication>>();
let activePopupWindow: Window | null = null;
let lastPopupLocation: string | null = null;
const popupClosedPollIntervalMs = 250;
// Grace window between "popup is closed" and declaring the flow cancelled: the
// bridge page closes the popup itself after broadcasting the response, and the
// main window still needs time to process it and run the code exchange.
const popupClosedGraceMs = 3000;

// Application Insights severity levels (see @microsoft/applicationinsights-web SeverityLevel)
const traceSeverity = { information: 1, warning: 2, error: 3 } as const;

function trackLoginTrace(message: string, severityLevel: number, properties?: Record<string, unknown>) {
	const { trackTrace } = useObservability();
	trackTrace({ message, severityLevel }, properties);
}

// MSAL clients are cached per B2C flow; a failed creation is evicted from the
// cache so the next call retries from scratch.
export function getMsalClient(flow: AzureADB2CFlow = "signUpSignInPolicyId") {
	const existingClient = clientPromises.get(flow);
	if (existingClient) return existingClient;

	const clientPromise = createMsalClient(flow).catch((error) => {
		trackLoginTrace("MSAL client creation failed", traceSeverity.error, {
			flow,
			errorCode: (error as AuthError).errorCode,
			subError: (error as AuthError).subError
		});
		clientPromises.delete(flow);
		throw error;
	});
	clientPromises.set(flow, clientPromise);
	return clientPromise;
}

async function createMsalClient(flow: AzureADB2CFlow) {
	await ensureSetupSettings();
	const { adb2cAuth } = useSettings();
	const adb2cAppSettings = adb2cAuth.value;
	if (!adb2cAppSettings || adb2cAppSettings === undefined) throw new Error("Azure AD B2C settings are not set");

	const clientId = adb2cAppSettings.clientId;
	const authority = adb2cAppSettings.instance;
	const domain = adb2cAppSettings.domain;
	const server = `${authority}/tfp/${domain}`;
	const flowId = adb2cAppSettings.flows[flow];

	const msalInstance = new PublicClientApplication({
		auth: {
			clientId,
			authority: `${server}/${flowId}`,
			knownAuthorities: [authority],
			redirectUri: `${location.origin}/redirect`
		},
		cache: {
			cacheLocation: "localStorage"
		},
		system: {
			popupBridgeTimeout: 60000,
			iframeBridgeTimeout: 60000
		}
	});
	msalInstance.addEventCallback((event) => {
		if (event.eventType !== EventType.POPUP_OPENED) return;

		// Hand the popup window to the close watcher and to the app-level
		// tracking (overlay, focus, navigation guard).
		const popupWindow = (event.payload as { popupWindow?: Window } | null)?.popupWindow;
		if (!popupWindow) return;

		activePopupWindow = popupWindow;
		setAzurePopupWindow(popupWindow);
	});
	await msalInstance.initialize();
	return msalInstance;
}
function getMsalScopes() {
	const { adb2cAuth } = useSettings();
	const adb2cAppSettings = adb2cAuth.value;
	if (!adb2cAppSettings) throw new Error("Azure AD B2C settings are not set");
	return [
		"openid",
		"profile",
		`https://${adb2cAppSettings.domain}/${adb2cAppSettings.registrationAppName}/User.Read`,
		`https://${adb2cAppSettings.domain}/${adb2cAppSettings.registrationAppName}/User.Write`
	];
}
function readPopupLocation(popup: Window): string {
	try {
		return `${popup.location.origin}${popup.location.pathname}`;
	} catch {
		return "cross-origin";
	}
}

/**
 * Rejects with a "user_cancelled" AuthError when the popup is closed before the
 * redirect bridge page broadcasts the response (see the MSAL contract above).
 * The popup reference arrives asynchronously in POPUP_OPENED, so the watcher
 * waits for it. Errors while reading popup state (e.g. a COOP-severed window
 * handle) mean "state unknown", so polling continues.
 *
 * Note the grace window: the bridge page closes the popup itself right after
 * broadcasting the response, while the main window still has to process the
 * message and run the code exchange. Rejecting on close alone would cancel
 * successful logins, so we only declare cancellation once popupClosedGraceMs
 * has elapsed without the MSAL promise settling (stop() clears the timeout).
 */
function watchPopupClosed() {
	let intervalId = 0;
	let graceTimeoutId = 0;
	const promise = new Promise<never>((_, reject) => {
		intervalId = window.setInterval(() => {
			const popup = activePopupWindow;
			if (!popup) return; // POPUP_OPENED not emitted yet

			lastPopupLocation = readPopupLocation(popup);

			let closed = false;
			try {
				closed = popup.closed;
			} catch {
				// window state unknown, keep waiting
			}
			if (closed) {
				window.clearInterval(intervalId);
				activePopupWindow = null;
				graceTimeoutId = window.setTimeout(() => {
					reject(new AuthError("user_cancelled", "Popup window was closed before the authentication response was received"));
				}, popupClosedGraceMs);
			}
		}, popupClosedPollIntervalMs);
	});
	return {
		promise,
		stop: () => {
			window.clearInterval(intervalId);
			window.clearTimeout(graceTimeoutId);
		}
	};
}

export async function loginPopup(onStartAction?: "signup") {

	async function launchPopup(flow: AzureADB2CFlow = "signUpSignInPolicyId") {
		const companyId = useCompanyStore().Id + "";
		const msal = await getMsalClient(flow);
		if (!msal)
			throw new Error("Missing MSAL client instance");

		const LangStore = useLangStore();
		const popupWidth = 530;
		const popupHeight = 830;
		const scopes = getMsalScopes();
		const systemId = Number(useSystemStore().info?.Id);
		const resultPromise = msal.loginPopup({
			scopes,
			// MSAL v5: let a new attempt cancel a dangling in-progress one instead
			// of throwing interaction_in_progress until the bridge timeout expires
			overrideInteractionInProgress: true,
			extraQueryParameters: {
				ui_locales: LangStore.isoCode ?? LangStore.defaultIsoCode,
				companyId,
				systemId: systemId.toString(),
				openMode: "popup",
				onStartAction: onStartAction ?? ""
			},
			popupWindowAttributes: {
				popupSize: { width: popupWidth, height: popupHeight }
			},
			prompt: "login"
		});
		return {
			scopes,
			msal,
			resultPromise
		};
	}

	try {
		activePopupWindow = null;
		lastPopupLocation = null;
		const popup = await launchPopup();
		const popupClosedWatcher = watchPopupClosed();
		let result: AuthenticationResult | null;
		let error: IAuthError | null;
		try {
			[result, error] = await resolveAny<AuthenticationResult, IAuthError>(
				Promise.race([popup.resultPromise, popupClosedWatcher.promise])
			);
		} finally {
			popupClosedWatcher.stop();
		}

		// Expected ways the flow can end without a session: the user closed the
		// popup or declined on the B2C page, or a newer attempt superseded this
		// one. lastPopupLocation is the popup's last known whereabouts and is
		// what exposes B2C-side bounces (e.g. redirect_uri_mismatch).
		const expectedEndings = ["user_cancelled", "interaction_in_progress", "interaction_in_progress_cancelled", "access_denied"];
		if (error && expectedEndings.includes(error.errorCode)) {
			trackLoginTrace("Login popup ended without a result", traceSeverity.warning, {
				errorCode: error.errorCode,
				subError: (error as AuthError).subError,
				lastPopupLocation
			});
			return;
		}
		if (error) throw error;

		if (!result) {
			trackUserEvent(AppInsightsEvent.UserLoginError, { error });
			return;
		}

		setAuthenticationResult(popup.msal, result);
		appStore.setLoadingView(true);
		await syncLoggedUserPrivacy();
		appStore.setLoadingView(false);
		trackUserEvent(AppInsightsEvent.UserLoginSuccessful);
	} catch (e) {
		const errorCode = (e as AuthError).errorCode;
		trackLoginTrace("Login failed", traceSeverity.error, { errorCode, subError: (e as AuthError).subError });
		if (errorCode && errorCode === "popup_window_error")
			await notifyUserPopupsBlocked();
	}
}
/**
 * Applies the outcome of a login attempt to the MSAL client and the app stores.
 * A null result clears every trace of the session (including persisted data).
 */
export function setAuthenticationResult(msal: PublicClientApplication, result: AuthenticationResult | null) {
	if (result) {
		msal.setActiveAccount(result.account);
		appStore.setAccessTokenInfo(result);
		const claims = result?.idTokenClaims as IIdTokenClaims | undefined;
		if (result.accessToken && claims) {
			useUserStore().setUserData({
				AccessToken: result.accessToken,
				FirstName: claims.given_name,
				LastName: claims.family_name,
				Email: claims.emails[claims.emails.length - 1],
				Phone: claims.extension_Phone,
				AccountInfo: result.account
			});
		} else useUserStore().clearData();
	} else {
		msal.setActiveAccount(null);
		appStore.setAccessTokenInfo(null);
		sessionStorage.clear();
		useUserStore().clearData();
		useUserStore().clearPersistedData();
	}
}
export async function tryGetUserSessionTokens() {
	const msal = await getMsalClient();
	if (!msal) {
		return {
			result: null,
			error: new Error("Missing MSAL client instance"),
			msal: null
		};
	}
	const scopes = getMsalScopes();
	const [result, error] = await resolveAny<AuthenticationResult, InteractionRequiredAuthError | Error>(
		msal.acquireTokenSilent({ scopes })
	);
	return { result, error, msal };
}
export async function logout() {
	const UserStore = useUserStore();
	if (!UserStore.isLoggedIn()) return;
	const msal = await getMsalClient();
	const home = getReservationHome({ currentName: router.currentRoute.value.name });
	await msal?.logoutPopup({
		postLogoutRedirectUri: `${location.origin}/redirect`,
		mainWindowRedirectUri: home.href
	});
	msal?.setActiveAccount(null);
	sessionStorage.clear();
	UserStore.clearData();
}
export async function notifyUserSessionExpiredLogout() {
	if (!useUserStore().isSessionExpired()) return Promise.resolve();

	const { translateKey } = usei18n();
	await modalOk([
		h("div", translateKey("weboffers_session_expired")),
		h("div", translateKey("weboffers_session_new"))
	], {
		title: translateKey("label_information"),
		"data-test": "Modal-SessionExpired"
	});
	logout();
}
let _isAskingForPrivacy: Promise<boolean | null | undefined> | null = null;
export function notifyUserPrivacyMissing(companyId: number, centerName: string) {
	if (_isAskingForPrivacy) return _isAskingForPrivacy;

	_isAskingForPrivacy = modalPrivacy(companyId, centerName);
	return _isAskingForPrivacy;
}
export async function syncLoggedUserPrivacy() {
	const userStore = useUserStore();
	const companyId = useCompanyStore().Id;
	const systemId = Number(useSystemStore().info?.Id);
	const systemName = useSystemStore().info?.Name ?? "";
	if (!userStore.isLoggedIn()) return;

	await userStore.ensureCustomerInfo();

	const acceptedPrivacy = userStore.CustomerInfo?.PrivacyChoices.find(el => el.PrivacyName === PolicyType.TermsAndConditions && el.Accepted);
	const acceptedOrDeclinedOffers = userStore.CustomerInfo?.PrivacyChoices.find(el => el.PrivacyName === PolicyType.ReceiveOffers);

	if (userStore.isLinkedToCompany && acceptedPrivacy && acceptedOrDeclinedOffers) {
		if (userStore.isLinkedToSystem) return;

		await userStore.syncLinkToSystem({ companyId, systemId });
		return;
	}

	const appRouter = ref(router);
	if (appRouter.value.currentRoute.name !== cartPage) {
		await timeout(1500);
		await notifyUserPrivacyMissing(companyId, systemName);
	}
}
let _isNotifyingBlockedPopups: Promise<boolean | null | undefined> | null = null;
export function notifyUserPopupsBlocked() {
	if (_isNotifyingBlockedPopups) return _isNotifyingBlockedPopups;

	const { translateKey } = usei18n();
	_isNotifyingBlockedPopups = modalWarning([
		h("div", translateKey("common_error_popup_blocked_text"))
	], {
		title: translateKey("common_error_popup_blocked_title"),
		"data-test": "Modal-PopupsBlocked"
	});
	return _isNotifyingBlockedPopups;
}
